import type { createBrowserRouter } from 'react-router';
import { isNative, sendToNative } from '@/shared/bridge';
import { ROUTES } from '@/shared/routing';
import { buildNavPath } from './nav-path';
import type { Navigator } from './types';
import { NAV_QUERY_KEYS } from './url-params';

type AppRouter = ReturnType<typeof createBrowserRouter>;

export interface NavigatorDeps {
  /** 이미 만들어진 RR router 인스턴스. composition root 가 router 생성 후 navigator 를 만든다. */
  router: AppRouter;
}

/**
 * `Navigator` factory — 명령형 navigation 의 SSOT.
 *
 * 의존: router 인스턴스 (composition root 가 주입) + bridge (`isNative`/`sendToNative`,
 * 자체 module singleton 이라 직접 import). 슬라이스 의존 0 이라 본 factory 가
 * shared/navigation 안에 거주해도 FSD 의존 방향 위반 없음.
 *
 * 책임:
 *   - URL 형태 조립 (redirectUrl encode, reason query 등)
 *   - 자기 자신으로의 navigate 방어 (`goToUnauthorized`)
 *   - external URL 의 web/native 분기 (`isNative` 게이트 + bridge `app.openExternal`)
 *
 * 룰 (ADR `navigator-service`):
 *   - intent only — 도메인-specific intent (`goToPaymentCheckout` 등) 추가 금지.
 *   - declarative (`<Link>`, `<Navigate>`, loader `redirect()`) 는 RR 관용 그대로.
 */
export function createNavigator({ router }: NavigatorDeps): Navigator {
  return {
    push(path) {
      void router.navigate(path);
    },
    replace(path) {
      void router.navigate(path, { replace: true });
    },
    goBack() {
      void router.navigate(-1);
    },

    goToLogin({ reason, redirectUrl } = {}) {
      // URL 조립은 `buildNavPath` SSOT — loader 의 declarative redirect 와 모양 공유.
      // `reason` 은 `default`(생략)이 기본이라 호출처가 의미 있을 때만 싣는다 (buildNavPath 가 falsy 누락).
      void router.navigate(
        buildNavPath(ROUTES.AUTH_LOGIN, {
          [NAV_QUERY_KEYS.REASON]: reason === 'default' ? undefined : reason,
          [NAV_QUERY_KEYS.REDIRECT_URL]: redirectUrl,
        }),
        {
          replace: true,
        },
      );
    },

    goToUnauthorized({ reason, redirectUrl }) {
      // 자기 자신으로의 navigate 만 방어 — 이미 unauthorized 페이지면 무한 navigate 차단.
      // composition.ts 의 `isSelfLoopPathname` 보다 좁은 검사 (의도): /login 이나 다른
      // /error/* 에서 본 메서드가 호출되면 (예: token-login 실패) /error/unauthorized 로
      // 가야 하므로 skip 하면 안 됨.
      if (router.state.location.pathname === ROUTES.ERROR_UNAUTHORIZED) return;

      void router.navigate(
        buildNavPath(ROUTES.ERROR_UNAUTHORIZED, {
          [NAV_QUERY_KEYS.REASON]: reason,
          [NAV_QUERY_KEYS.REDIRECT_URL]: redirectUrl,
        }),
        { replace: true },
      );
    },

    openExternal(url, opts = {}) {
      if (isNative()) {
        // mode 미지정이면 native 기본(`in-app`) — 프로토콜 optional 이라 그대로 흘려보낸다.
        sendToNative({ type: 'app.openExternal', payload: { url, mode: opts.mode } });
        return;
      }
      // web: 새 탭 + opener 차단 + referrer 차단 (보안 default)
      window.open(url, opts.target ?? '_blank', 'noopener,noreferrer');
    },
  };
}
